/**
 * Every URL the frontend can call, in one place.
 *
 * This is the contract your Spring Boot `@RestController` methods must satisfy.
 * See `docs/API_CONTRACT.md` for the request/response shape of each entry.
 * Paths are relative to VITE_API_BASE_URL (default `/api/v1`).
 */
export const endpoints = {
  auth: {
    login: '/auth/login',
    logout: '/auth/logout',
    refresh: '/auth/refresh',
    me: '/auth/me',
    changePassword: '/auth/change-password',
  },
  /** Permission-filtered sidebar + scanner tabs. Replaces the old static array. */
  navigation: {
    current: '/navigation',
    admin: '/navigation/admin',
    adminItem: (id: number | string) => `/navigation/admin/${id}`,
  },
  rbac: {
    roles: '/roles',
    role: (id: number | string) => `/roles/${id}`,
    rolePermissions: (id: number | string) => `/roles/${id}/permissions`,
    permissions: '/permissions',
  },
  tenants: {
    register: '/tenants/register',
    current: '/tenants/current',
    slugAvailable: '/tenants/slug-available',
    /** Platform operators only (`TENANT_VIEW`/`TENANT_MANAGE`). */
    list: '/tenants',
    byId: (id: number | string) => `/tenants/${id}`,
  },
  dashboard: {
    summary: '/dashboard/summary',
    dailyActivity: '/dashboard/daily-activity',
    qrStatus: '/dashboard/qr-status',
    buyerWise: '/dashboard/buyer-wise',
    recentActivities: '/dashboard/recent-activities',
    poSummary: '/dashboard/po-summary',
    todaySummary: '/dashboard/today-summary',
  },
  master: {
    buyers: '/buyers',
    buyer: (id: number | string) => `/buyers/${id}`,
    purchaseOrders: '/purchase-orders',
    purchaseOrder: (id: number | string) => `/purchase-orders/${id}`,
    styles: '/styles',
    style: (id: number | string) => `/styles/${id}`,
    lotNumbers: '/lot-numbers',
    lotNumber: (id: number | string) => `/lot-numbers/${id}`,
    sizes: '/sizes',
    size: (id: number | string) => `/sizes/${id}`,
    warehouses: '/warehouses',
  },
  qr: {
    generate: '/qr-codes/generate',
    list: '/qr-codes',
    byCode: (code: string) => `/qr-codes/${code}`,
    batches: '/qr-codes/batches',
    batch: (id: number | string) => `/qr-codes/batches/${id}`,
    batchPdf: (id: number | string) => `/qr-codes/batches/${id}/labels.pdf`,
    cancel: (code: string) => `/qr-codes/${code}/cancel`,
  },
  cartons: {
    list: '/cartons',
    byCode: (code: string) => `/cartons/${code}`,
    timeline: (code: string) => `/cartons/${code}/timeline`,
    validateReceive: '/cartons/receive/validate',
    receive: '/cartons/receive',
    lotCurrent: '/cartons/lots/current',
    lots: '/cartons/lots',
    lot: (lotId: number | string) => `/cartons/lots/${lotId}`,
    lotScan: (lotId: number | string) => `/cartons/lots/${lotId}/scan`,
    receipts: '/cartons/receipts',
    dispatchLots: '/cartons/dispatch/lots',
    dispatchLotCurrent: '/cartons/dispatch/lots/current',
    dispatchLotOpen: (lotId: number | string) => `/cartons/dispatch/lots/${lotId}/open`,
    validateDispatch: '/cartons/dispatch/validate',
    dispatch: '/cartons/dispatch',
    dispatches: '/cartons/dispatches',
  },
  commercialInfo: {
    list: '/lots/commercial-info',
    byLot: (lotId: number | string) => `/lots/commercial-info/${lotId}`,
  },
  shipments: {
    list: '/shipments',
    byId: (id: number | string) => `/shipments/${id}`,
  },
  scanHistory: '/scan-history',
  reports: {
    /** type: QR_GENERATION | RECEIVING | DISPATCH | STOCK | SHIPMENT */
    rows: (type: string) => `/reports/${type}`,
    totals: (type: string) => `/reports/${type}/totals`,
    export: (type: string) => `/reports/${type}/export`,
  },
  users: {
    list: '/users',
    byId: (id: number | string) => `/users/${id}`,
    status: (id: number | string) => `/users/${id}/status`,
    roles: (id: number | string) => `/users/${id}/roles`,
    resetPassword: (id: number | string) => `/users/${id}/reset-password`,
  },
  settings: '/settings',
} as const;
